import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Trophy, Flame, CheckCircle2, ArrowRight, ScrollText } from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import SiteFooter from "@/components/SiteFooter";

const CAT_LABEL = { skilling: "Skilling", quest: "Quest", quests: "Quest", diary: "Diary", diaries: "Achievement Diary", combat: "Combat", raids: "Raid", collection: "Collection", clues: "Treasure Trail" };

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function PublicProfile() {
  const { rsn } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardUrl = `${API}/public/u/${encodeURIComponent(rsn)}/card.png`;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/public/u/${encodeURIComponent(rsn)}`)
      .then(({ data }) => alive && setData(data))
      .catch((e) => alive && setError(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [rsn]);

  // SEO: reflect the player in the document title + meta description (Googlebot renders JS).
  useEffect(() => {
    if (!data) return;
    const prevTitle = document.title;
    document.title = `${data.rsn} · ${data.gamemode_label} on TaskScape`;
    const rankTxt = data.rank ? `Rank #${data.rank}` : "Climbing the ranks";
    const desc = `${rankTxt} · ${data.total_points.toLocaleString()} pts · ${data.streak}🔥 streak. See ${data.rsn}'s OSRS task progress on TaskScape and get your own free task board.`;
    let tag = document.querySelector('meta[name="description"]');
    const prevDesc = tag ? tag.getAttribute("content") : null;
    if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", "description"); document.head.appendChild(tag); }
    tag.setAttribute("content", desc);
    return () => { document.title = prevTitle; if (tag && prevDesc !== null) tag.setAttribute("content", prevDesc); };
  }, [data]);

  return (
    <div className="stone-bg App flex flex-col items-center px-4 py-10 min-h-screen" data-testid="public-profile-page">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-6">
          <Link to="/login" className="osrs-title text-3xl osrs-shadow inline-block" data-testid="public-brand-link">TaskScape</Link>
        </div>

        {loading && <p className="osrs-shadow text-[#E6A519] text-center py-16" data-testid="public-loading">Summoning the scroll...</p>}

        {!loading && error && (
          <div className="osrs-panel p-6 text-center" data-testid="public-error">
            <p className="osrs-shadow text-[#E6A519] mb-4">{error}</p>
            <Link to="/login" className="osrs-btn osrs-btn-green px-5 py-2 inline-flex items-center gap-2">
              Start tracking your OSRS account <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {!loading && data && (
          <>
            <div className="osrs-panel p-3 mb-5 overflow-hidden">
              <img src={cardUrl} alt={`${data.rsn} rank card`} className="w-full block border-2 border-black" data-testid="public-card-img" />
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="osrs-panel p-3 text-center" data-testid="public-stat-rank">
                <Trophy size={18} className="text-[#FFCF3F] mx-auto mb-1" />
                <div className="osrs-title text-2xl">{data.rank ? `#${data.rank}` : "—"}</div>
                <div className="osrs-shadow text-[11px] text-[#b9a06a]">RANK</div>
              </div>
              <div className="osrs-panel p-3 text-center" data-testid="public-stat-points">
                <ScrollText size={18} className="text-[#7CFC7C] mx-auto mb-1" />
                <div className="osrs-title text-2xl">{data.total_points.toLocaleString()}</div>
                <div className="osrs-shadow text-[11px] text-[#b9a06a]">POINTS</div>
              </div>
              <div className="osrs-panel p-3 text-center" data-testid="public-stat-streak">
                <Flame size={18} className="text-[#FF7A1A] mx-auto mb-1" />
                <div className="osrs-title text-2xl">{data.streak}</div>
                <div className="osrs-shadow text-[11px] text-[#b9a06a]">STREAK</div>
              </div>
            </div>

            <div className="osrs-panel p-4 mb-6">
              <h2 className="osrs-title text-xl mb-3 flex items-center gap-2"><CheckCircle2 size={18} /> Recent completions</h2>
              {data.recent.length === 0 ? (
                <p className="osrs-shadow text-[#b9a06a] text-sm">No verified completions yet.</p>
              ) : (
                <ul className="space-y-2" data-testid="public-recent-list">
                  {data.recent.map((t, i) => (
                    <li key={i} className="flex items-center justify-between border-b border-[#0f0d09]/40 pb-2 last:border-0">
                      <div className="min-w-0">
                        <p className="osrs-shadow text-[#E6A519] text-sm truncate">{t.title}</p>
                        <p className="osrs-shadow text-[11px] text-[#b9a06a]">{CAT_LABEL[t.category] || t.category} · {timeAgo(t.completed_at)}</p>
                      </div>
                      <span className="osrs-title text-[#7CFC7C] text-sm shrink-0 ml-3">+{t.points_awarded}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="osrs-panel p-6 text-center" data-testid="public-cta">
              <h3 className="osrs-title text-2xl mb-2">Think you can beat {data.rsn}?</h3>
              <p className="osrs-shadow text-[#b9a06a] text-sm mb-4">Get Slayer-styled tasks for your own OSRS account, climb the leaderboard, and finish #1 each month to win a free Bond.</p>
              <Link to="/login" className="osrs-btn osrs-btn-green px-6 py-3 inline-flex items-center gap-2 text-base" data-testid="public-signup-btn">
                Start your grind <ArrowRight size={18} />
              </Link>
            </div>
          </>
        )}

        <SiteFooter />
      </div>
    </div>
  );
}
